import {
    Box,
    Button,
    Checkbox,
    Menu,
    MenuItem,
    MuiIcons,
    Tooltip,
    Typography,
} from "@leap4/frontend";
import React, { useState } from "react";
import type { DashboardMetricDefDto } from "../../../api/generated/models";

const { Tune } = MuiIcons;

interface IProps {
    metrics: DashboardMetricDefDto[];
    selected: string[];
    max: number;
    onChange: (keys: string[]) => void;
}

/** Grouped so a long list stays scannable; order follows the catalogue within each group. */
const GROUP_ORDER = ["Economics", "Traffic", "Conversions"];

const MetricPicker = ({ metrics, selected, max, onChange }: IProps): JSX.Element => {
    const [anchor, setAnchor] = useState<HTMLElement | null>(null);
    const atCap = selected.length >= max;

    const toggle = (key: string): void => {
        if (selected.includes(key)) {
            // Never empty the chart — the last metric stays until another is chosen.
            if (selected.length > 1) onChange(selected.filter((k) => k !== key));
            return;
        }
        if (!atCap) onChange([...selected, key]);
    };

    const groups = GROUP_ORDER.map((g) => ({
        group: g,
        items: metrics.filter((m) => m.group === g),
    })).filter((g) => g.items.length > 0);

    return (
        <>
            <Button
                size="small"
                variant="outlined"
                startIcon={<Tune sx={{ fontSize: 16 }} />}
                onClick={(e: React.MouseEvent<HTMLElement>) => setAnchor(e.currentTarget)}
            >
                Metrics ({selected.length})
            </Button>
            <Menu anchorEl={anchor} open={!!anchor} onClose={() => setAnchor(null)}>
                {groups.map((g) => [
                    <Typography
                        key={`${g.group}-h`}
                        variant="caption"
                        sx={{
                            display: "block",
                            px: 2,
                            pt: 1,
                            color: "text.secondary",
                            textTransform: "uppercase",
                            letterSpacing: 0.4,
                            fontWeight: 600,
                        }}
                    >
                        {g.group}
                    </Typography>,
                    ...g.items.map((m) => {
                        const on = selected.includes(m.key);
                        return (
                            <MenuItem
                                key={m.key}
                                dense
                                disabled={!on && atCap}
                                onClick={() => toggle(m.key)}
                                sx={{ py: 0.25 }}
                            >
                                {/* Wrapping the row keeps the formula reachable without a second click. */}
                                <Checkbox size="small" checked={on} sx={{ p: 0.5, mr: 1 }} />
                                <Box
                                    sx={{
                                        width: 10,
                                        height: 2,
                                        mr: 1,
                                        bgcolor: m.color,
                                        opacity: on ? 1 : 0.3,
                                    }}
                                />
                                <Tooltip title={m.formula} placement="right" arrow>
                                    <Box sx={{ flex: 1, fontSize: 13 }}>{m.label}</Box>
                                </Tooltip>
                                <Box sx={{ fontSize: 10, color: "text.disabled", ml: 1 }}>{m.unit}</Box>
                            </MenuItem>
                        );
                    }),
                ])}
                <Typography
                    variant="caption"
                    sx={{ display: "block", px: 2, py: 1, color: "text.disabled", maxWidth: 260 }}
                >
                    {atCap
                        ? `${max} at once keeps the chart readable — remove one to add another.`
                        : "Metrics on a different scale get their own axis."}
                </Typography>
            </Menu>
        </>
    );
};

export default MetricPicker;
